import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { EnrollmentStatuses } from '@spike/membership-model';
import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import type { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import type { Boarding, Daycare, Discount, PetServiceWrapper } from 'model/NewMultipleBooking';
import type { BoardingOffer, BoardingService, BoardingServiceWrapper } from 'model/Service';
import { getEnrollmentEndDate, getEnrollmentStartDate } from 'utils/EnrollmentUtils';
import { getBoardingCatalogNightlyPrice, getBoardingStandardNightlyPrice } from '../Boarding/utils/boardingPricing';

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const parseEnrollmentDay = (value: unknown, tz: string): Moment | null => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const ddmmyyyy = moment(value, 'DD/MM/YYYY', true);
        if (ddmmyyyy.isValid()) return ddmmyyyy.tz(tz).startOf('day');
    }
    const parsed = moment.isMoment(value) ? value.clone() : moment(value as moment.MomentInput);
    if (!parsed.isValid()) return null;
    return parsed.tz(tz).startOf('day');
};

export function isSubscriptionEnrollment(enrollment: Enrollment | undefined): boolean {
    if (!enrollment) return false;
    const enrollmentType =
        (enrollment as { enrollmentType?: string }).enrollmentType ?? enrollment.membership?.enrollmentType;
    return enrollmentType === 'subscription';
}

/** Subscription redemptions apply only within the current billing period (strictly before next billing date). */
export function isWithinSubscriptionBillingPeriod(
    enrollment: Enrollment,
    selectedDate: Moment,
    timezone?: string
): boolean {
    const tz = timezone || selectedDate.tz?.() || moment.tz.guess();
    const selectedDay = selectedDate.clone().tz(tz).startOf('day');

    const periodStart = parseEnrollmentDay(getEnrollmentStartDate(enrollment), tz);
    if (periodStart?.isValid() && selectedDay.isBefore(periodStart, 'day')) {
        return false;
    }

    const nextBillingRaw =
        enrollment.nextBillingDate ?? (enrollment as { nextBillingDateText?: string }).nextBillingDateText;
    const nextBillingDay = parseEnrollmentDay(nextBillingRaw, tz);
    if (nextBillingDay?.isValid() && !selectedDay.isBefore(nextBillingDay, 'day')) {
        return false;
    }

    return true;
}

export function isMembershipDiscountBased(item?: MembershipItem | null): boolean {
    if (!item) return false;
    const pct = Number(item.discountPercentage);
    const amt = Number(item.discountAmount);
    return (Number.isFinite(pct) && pct > 0) || (Number.isFinite(amt) && amt > 0);
}

/** Unlimited package or discount memberships can redeem on every eligible appointment in a batch. */
export function shouldApplyMembershipToAllAppointments(
    item?: MembershipItem | null,
    enrollment?: Enrollment | null
): boolean {
    if (!item) return false;
    if (isMembershipDiscountBased(item)) return true;
    if (isSubscriptionEnrollment(enrollment ?? undefined)) {
        return false;
    }
    return Boolean(item.unlimited);
}

export function getMembershipRemainingUses(item?: MembershipItem | null, enrollment?: Enrollment | null): number {
    if (!item) return 0;

    if (isSubscriptionEnrollment(enrollment ?? undefined)) {
        if (typeof item.left === 'number') {
            return Math.max(0, item.left);
        }
        if (typeof item.available === 'number') {
            if (typeof item.consumed === 'number') {
                return Math.max(0, item.available - item.consumed);
            }
            return Math.max(0, item.available);
        }
        if (item.unlimited) return Number.POSITIVE_INFINITY;
        return 0;
    }

    if (item.unlimited) return Number.POSITIVE_INFINITY;

    if (typeof item.left === 'number') {
        return Math.max(0, item.left);
    }

    if (typeof item.available === 'number') {
        if (typeof item.consumed === 'number') {
            return Math.max(0, item.available - item.consumed);
        }
        return Math.max(0, item.available);
    }

    return 0;
}

export function resolveDaycareCatalogPrice(daycare: Daycare, catalogPriceOverride?: number): number {
    const candidates = [catalogPriceOverride, daycare.service?.beforePrice];
    for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value > 0) {
            return value;
        }
    }
    return 0;
}

export function isHourlyDaycareVariableDuration(variableDuration?: string): boolean {
    if (!variableDuration) return false;
    try {
        const parsed = JSON.parse(variableDuration) as { duration_option?: string; durationOption?: string };
        return (parsed.duration_option ?? parsed.durationOption) === 'hourly';
    } catch {
        return false;
    }
}

/** Per-appointment charge: catalog unit for full/half day, unit × billable hours for hourly. */
export function resolveDaycareSlotLinePrice(
    daycare: Daycare,
    catalogUnitPrice: number,
    durationInMinutes: number
): number {
    const isHourly = isHourlyDaycareVariableDuration(daycare.service?.variableDuration);
    const formPrice = Number(daycare.price);
    const catalog = catalogUnitPrice > 0 ? catalogUnitPrice : 0;
    const billableHours = durationInMinutes > 0 ? Math.max(1, Math.ceil(durationInMinutes / 60)) : 1;

    const hasEnteredPrice = Number.isFinite(formPrice) && formPrice >= 0;

    if (isHourly) {
        if (hasEnteredPrice) {
            return formPrice * billableHours;
        }
        if (catalog > 0) {
            return catalog * billableHours;
        }
    }

    if (catalog > 0 && formPrice > 0 && billableHours > 1 && Math.abs(formPrice - catalog * billableHours) < 0.02) {
        return catalog;
    }

    if (hasEnteredPrice) {
        return formPrice;
    }

    return catalog;
}

export function isMembershipIncludedVisit(item?: MembershipItem | null): boolean {
    return item != null && !isMembershipDiscountBased(item);
}

export function resolveDaycareSlotCatalogPrice(
    service?: PetServiceWrapper,
    itemDataPrice?: number,
    catalogFallback?: number
): number {
    const beforePrice = Number(service?.beforePrice);
    if (beforePrice > 0) {
        return beforePrice;
    }

    const itemPrice = Number(itemDataPrice);
    if (itemPrice > 0) {
        return itemPrice;
    }

    const fallback = Number(catalogFallback);
    if (fallback > 0) {
        return fallback;
    }

    const membershipRedeemed = service?.enrollment?.id != null;
    const servicePrice = Number(service?.price);
    if (!membershipRedeemed && servicePrice > 0) {
        return servicePrice;
    }

    return servicePrice > 0 ? servicePrice : 0;
}

/** Whether the enrollment is active and the appointment date falls within its valid range. */
export function isEnrollmentAvailableOnDate(
    enrollment: Enrollment | undefined,
    selectedDate: Moment | undefined,
    timezone?: string
): boolean {
    if (!enrollment || !selectedDate?.isValid?.()) return false;

    const tz = timezone || selectedDate.tz?.() || moment.tz.guess();
    const selectedDay = selectedDate.clone().tz(tz).startOf('day');

    if (enrollment.status === EnrollmentStatuses.pending) return false;
    if (enrollment.status === EnrollmentStatuses.pastDue) return false;
    if (enrollment.status === EnrollmentStatuses.paused) return false;
    if (enrollment.membership?.active === false) return false;

    const isCanceled = enrollment.status === EnrollmentStatuses.canceled;
    if (isCanceled) {
        const canceledEnd = parseEnrollmentDay(getEnrollmentEndDate(enrollment), tz);
        if (!canceledEnd?.isValid() || selectedDay.isAfter(canceledEnd.clone().endOf('day'), 'day')) return false;
    } else if (enrollment.status !== EnrollmentStatuses.active) {
        return false;
    }

    const start = parseEnrollmentDay(getEnrollmentStartDate(enrollment), tz);
    const end = parseEnrollmentDay(getEnrollmentEndDate(enrollment), tz);

    if (start?.isValid() && selectedDay.isBefore(start, 'day')) return false;
    if (end?.isValid() && selectedDay.isAfter(end.clone().endOf('day'), 'day')) return false;

    if (isSubscriptionEnrollment(enrollment) && !isWithinSubscriptionBillingPeriod(enrollment, selectedDate, tz)) {
        return false;
    }

    return true;
}

/** Price the membership discount is applied to: custom/form when set, otherwise catalog. */
export function resolveMembershipBasePrice(catalogPrice: number, formOrCustomPrice?: number | null): number {
    const form = Number(formOrCustomPrice);
    if (Number.isFinite(form) && form > 0) {
        return form;
    }
    const catalog = Number(catalogPrice);
    return Number.isFinite(catalog) && catalog > 0 ? catalog : 0;
}

export function applyAppointmentCardManualDiscount(
    base: number,
    discount?: { type?: string; value?: number } | null
): number | undefined {
    if (!discount || discount.type === 'none' || discount.value == null || discount.value === 0) {
        return undefined;
    }
    const total = Number(base) || 0;
    if (discount.type === 'percentage') {
        return roundMoney(total - (discount.value * total) / 100);
    }
    if (discount.type === 'amount') {
        return roundMoney(Math.max(0, total - discount.value));
    }
    return undefined;
}

export function resolveAppointmentCardLineBase(params: {
    slotPrice: number;
    itemDataPrice?: number | null;
    beforePrice?: number | null;
    customPriceAmount?: number | null;
    hourlyMultiplier?: number;
}): number {
    const custom = Number(params.customPriceAmount);
    const itemPrice = Number(params.itemDataPrice);
    const before = Number(params.beforePrice);
    const slot = Number(params.slotPrice) || 0;

    let unitBase = slot;
    if (params.customPriceAmount != null && Number.isFinite(custom) && custom >= 0) {
        unitBase = custom;
    } else if (params.itemDataPrice != null && Number.isFinite(itemPrice) && itemPrice >= 0) {
        unitBase = itemPrice;
    } else if (Number.isFinite(before) && before > 0) {
        unitBase = before;
    }

    const hours = Number(params.hourlyMultiplier);
    if (Number.isFinite(hours) && hours > 1) {
        return roundMoney(unitBase * hours);
    }
    return unitBase;
}

/** Appointment card line: $0 for included/availability redeem, membership-discounted price otherwise. */
export function resolveAppointmentCardLineDisplay(params: {
    enrollmentId?: number | null;
    membershipItem?: MembershipItem | null;
    slotPrice: number;
    basePrice: number;
    itemDiscount?: { type?: string; value?: number } | null;
}): {
    membershipApplied: boolean;
    displayPrice: number;
    originalPrice: number;
    hasDisplayDiscount: boolean;
} {
    const slot = Number(params.slotPrice) || 0;
    const base = Number(params.basePrice) > 0 ? Number(params.basePrice) : slot;
    const membershipApplied = params.enrollmentId != null;

    if (!membershipApplied) {
        const discounted = applyAppointmentCardManualDiscount(slot, params.itemDiscount);
        return {
            membershipApplied: false,
            displayPrice: discounted ?? slot,
            originalPrice: slot,
            hasDisplayDiscount: (params.itemDiscount?.value ?? 0) !== 0
        };
    }

    const displayPrice = params.membershipItem
        ? computeMembershipDisplayPrice(base, params.membershipItem).displayPrice
        : applyAppointmentCardManualDiscount(base, params.itemDiscount) ?? 0;
    const originalPrice = base > 0 ? base : slot;

    return {
        membershipApplied: true,
        displayPrice,
        originalPrice,
        hasDisplayDiscount: originalPrice > displayPrice + 0.004
    };
}

export function computeMembershipDisplayPrice(
    catalogPrice: number,
    membershipItem?: MembershipItem | null
): { displayPrice: number; catalogPrice: number } {
    const catalog = Number(catalogPrice) || 0;
    if (!membershipItem) {
        return { displayPrice: catalog, catalogPrice: catalog };
    }

    if (membershipItem.discountPercentage != null && membershipItem.discountPercentage > 0) {
        const pct = membershipItem.discountPercentage;
        return {
            catalogPrice: catalog,
            displayPrice: roundMoney(catalog * (1 - pct / 100))
        };
    }

    if (membershipItem.discountAmount != null && membershipItem.discountAmount > 0) {
        return {
            catalogPrice: catalog,
            displayPrice: roundMoney(Math.max(0, catalog - membershipItem.discountAmount))
        };
    }

    return { catalogPrice: catalog, displayPrice: 0 };
}

export function computeMembershipPreviewDiscount(
    catalogPrice: number,
    membershipItem?: MembershipItem | null
): ValueTypeSelectorValue | null {
    if (!membershipItem) return null;

    if (membershipItem.discountPercentage != null && membershipItem.discountPercentage > 0) {
        return { type: 'percentage', value: membershipItem.discountPercentage };
    }

    if (membershipItem.discountAmount != null && membershipItem.discountAmount > 0) {
        return { type: 'amount', value: membershipItem.discountAmount };
    }

    return { type: 'percentage', value: 100 };
}

export function applyMembershipDaycarePricing(
    wrapper: PetServiceWrapper,
    enrollment?: Enrollment,
    membershipItem?: MembershipItem | null,
    previous?: PetServiceWrapper
): PetServiceWrapper {
    return applyMembershipCatalogPriceWithPreviewDiscount(wrapper, enrollment, membershipItem, previous).wrapper;
}

export function applyMembershipCatalogPriceWithPreviewDiscount(
    wrapper: PetServiceWrapper,
    enrollment?: Enrollment,
    membershipItem?: MembershipItem | null,
    previous?: PetServiceWrapper
): { wrapper: PetServiceWrapper; previewDiscount: ValueTypeSelectorValue | null } {
    const enrollmentToUse = enrollment ?? previous?.enrollment;
    const itemToUse = membershipItem ?? previous?.membershipItem;
    const catalog = wrapper.beforePrice ?? wrapper.price;
    const membershipBase = resolveMembershipBasePrice(catalog, wrapper.price);

    const next: PetServiceWrapper = {
        ...wrapper,
        price: membershipBase,
        beforePrice: membershipBase
    };

    if (enrollmentToUse != null) {
        next.enrollment = enrollmentToUse;
    }
    if (itemToUse != null) {
        next.membershipItem = itemToUse;
    }

    const previewDiscount =
        enrollmentToUse != null && itemToUse != null
            ? computeMembershipPreviewDiscount(membershipBase, itemToUse)
            : null;

    return { wrapper: next, previewDiscount };
}

export function applyMembershipBoardingPricing(
    wrapper: PetServiceWrapper,
    enrollment?: Enrollment,
    membershipItem?: MembershipItem | null,
    previous?: PetServiceWrapper
): PetServiceWrapper {
    const enrollmentToUse = enrollment ?? previous?.enrollment;
    const itemToUse = membershipItem ?? previous?.membershipItem;
    const catalog = wrapper.beforePrice ?? wrapper.price;
    const membershipBase = resolveMembershipBasePrice(catalog, wrapper.price);

    const next: PetServiceWrapper = {
        ...wrapper,
        price: membershipBase,
        beforePrice: membershipBase
    };

    if (enrollmentToUse != null) {
        next.enrollment = enrollmentToUse;
    }
    if (itemToUse != null) {
        next.membershipItem = itemToUse;
    }

    return next;
}

export function resolveBoardingNightlyDisplayPrice(
    catalogNightlyPrice: number,
    appliedRateNightlyPrice: number,
    membershipItem?: MembershipItem | null,
    hasSelectedDeal?: boolean
): number {
    if (hasSelectedDeal) {
        return appliedRateNightlyPrice;
    }
    if (!membershipItem) {
        return appliedRateNightlyPrice;
    }
    return computeMembershipDisplayPrice(catalogNightlyPrice, membershipItem).displayPrice;
}

/** Membership discount applies to custom nightly when set, otherwise catalog; rate/rate_rule and membership are not stackable. */
export function resolveBoardingNightlyPriceFromService(
    service: BoardingServiceWrapper | BoardingService,
    membershipItem?: MembershipItem | null,
    customNightlyPrice?: number
): number {
    const catalogNightly = getBoardingCatalogNightlyPrice(service);
    const standardRateNightly = getBoardingStandardNightlyPrice(service);
    const selectedOffer = 'selectedOffer' in service ? service.selectedOffer : undefined;
    if (selectedOffer != null) {
        return selectedOffer.pricePerNight;
    }
    const membershipBase = resolveMembershipBasePrice(catalogNightly, customNightlyPrice);
    if (!membershipItem) {
        return membershipBase > 0 && customNightlyPrice != null ? membershipBase : standardRateNightly;
    }
    return computeMembershipDisplayPrice(membershipBase, membershipItem).displayPrice;
}

export function isMembershipNightlyHigherThanAppliedRate(
    catalogNightlyPrice: number,
    appliedRateNightlyPrice: number,
    membershipItem?: MembershipItem | null
): boolean {
    if (!membershipItem) return false;
    const membershipNightly = computeMembershipDisplayPrice(catalogNightlyPrice, membershipItem).displayPrice;
    return membershipNightly > appliedRateNightlyPrice + 0.004;
}

export function withEnrollmentOnly(
    wrapper: PetServiceWrapper,
    enrollment?: Enrollment,
    membershipItem?: MembershipItem | null,
    previous?: PetServiceWrapper
): PetServiceWrapper {
    const next: PetServiceWrapper = { ...wrapper };
    const e = enrollment ?? previous?.enrollment;
    if (e != null) next.enrollment = e;
    const item = membershipItem ?? previous?.membershipItem;
    if (item != null) next.membershipItem = item;
    return next;
}

export function applyMembershipPreviewToBoardingLineTotal(
    lineTotal: number,
    membershipItem?: MembershipItem | null
): number {
    return computeMembershipDisplayPrice(lineTotal, membershipItem).displayPrice;
}

export function computeBoardingMembershipStayTotal(
    catalogNightlyPrice: number,
    nights: number,
    membershipItem?: MembershipItem | null
): number | null {
    if (!membershipItem) return null;
    const nightCount = Math.max(0, nights);
    const catalogLineTotal = roundMoney((Number(catalogNightlyPrice) || 0) * nightCount);
    return applyMembershipPreviewToBoardingLineTotal(catalogLineTotal, membershipItem);
}

export function isDealTotalHigherThanMembershipStay(
    dealTotal: number,
    catalogNightlyPrice: number,
    nights: number,
    membershipItem?: MembershipItem | null
): boolean {
    const membershipTotal = computeBoardingMembershipStayTotal(catalogNightlyPrice, nights, membershipItem);
    if (membershipTotal == null) return false;
    return Number(dealTotal) > membershipTotal + 0.004;
}

const usesMembershipDiscountFieldPreview = (service?: PetServiceWrapper): boolean =>
    service?.enrollment?.id != null && service?.membershipItem != null && service.businessArea !== 'boarding';

export function boardingHasSelectedDeal(service?: PetServiceWrapper): boolean {
    return service?.businessArea === 'boarding' && service.selectedOffer != null;
}

export function shouldApplyMembershipRedeemOnSave(service?: PetServiceWrapper): boolean {
    if (service?.enrollment?.id == null) return false;
    if (boardingHasSelectedDeal(service)) return false;
    return true;
}

export function resolveDaycareHourlyUnitPriceForSave(service?: PetServiceWrapper): number | undefined {
    if (!service || !isHourlyDaycareVariableDuration(service.variableDuration)) {
        return undefined;
    }

    const editedPrice = Number(service.price);
    if (Number.isFinite(editedPrice) && editedPrice === 0) {
        return 0;
    }

    const unitPrice = Number(service.beforePrice);
    if (unitPrice > 0) {
        return unitPrice;
    }

    const hours = Math.max(1, Math.ceil((service.durationInMinutes || 0) / 60));
    const linePrice = Number(service.price) || 0;
    if (linePrice > 0 && hours > 0) {
        return linePrice / hours;
    }

    return linePrice > 0 ? linePrice : undefined;
}

export function resolveOrderItemPriceForSave(service?: PetServiceWrapper): number | undefined {
    if (!service) return undefined;

    if (boardingHasSelectedDeal(service)) {
        return service.selectedOffer?.pricePerNight ?? service.price;
    }

    const catalog =
        service.businessArea === 'boarding'
            ? getBoardingCatalogNightlyPrice({
                  beforePrice: service.beforePrice,
                  price: service.price
              })
            : service.beforePrice ?? service.price;

    const membershipBase = resolveMembershipBasePrice(catalog, service.customPriceAmount);

    const hourlyUnitPrice = resolveDaycareHourlyUnitPriceForSave(service);
    if (hourlyUnitPrice != null) {
        const hourlyMembershipBase = resolveMembershipBasePrice(hourlyUnitPrice, service.customPriceAmount);
        if (service.enrollment?.id == null) {
            return hourlyMembershipBase;
        }

        if (usesMembershipDiscountFieldPreview(service)) {
            return hourlyMembershipBase;
        }

        if (service.membershipItem) {
            const { displayPrice } = computeMembershipDisplayPrice(hourlyMembershipBase, service.membershipItem);
            const current = Number(service.price) || 0;
            const isManualOverride =
                Math.abs(current - displayPrice) > 0.004 && Math.abs(current - hourlyMembershipBase) > 0.004;
            if (isManualOverride) {
                const hours = Math.max(1, Math.ceil((service.durationInMinutes || 0) / 60));
                return current / hours;
            }
        }

        return hourlyMembershipBase;
    }

    if (service.enrollment?.id == null) {
        return service.price;
    }

    if (usesMembershipDiscountFieldPreview(service)) {
        return membershipBase;
    }

    if (service.membershipItem) {
        const { displayPrice } = computeMembershipDisplayPrice(membershipBase, service.membershipItem);
        const current = Number(service.price) || 0;
        const isManualOverride = Math.abs(current - displayPrice) > 0.004 && Math.abs(current - membershipBase) > 0.004;
        if (isManualOverride) {
            return current;
        }
    }

    return membershipBase;
}

export function resolveBoardingOrderItemPriceForSave(boarding: Boarding): number {
    if (boarding.service?.selectedOffer != null) {
        return boarding.service.selectedOffer.pricePerNight;
    }

    if (boarding.enrollment?.id != null) {
        const catalogNightly = getBoardingCatalogNightlyPrice({
            catalogPrice: boarding.service?.catalogPrice,
            beforePrice: boarding.service?.beforePrice,
            price: boarding.price
        });
        return resolveMembershipBasePrice(catalogNightly, boarding.service?.beforePrice);
    }

    return boarding.price;
}

export function resolveDaycareSavePricing(daycare: Daycare): {
    orderItemPrice: number | undefined;
    discountAmount?: string;
    discountPercentage?: number;
    enrollmentId?: number;
} {
    const itemDiscount: Discount = {
        type: (daycare.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
        value: daycare.discount?.value ?? 0
    };
    const durationInMinutes =
        daycare.from && daycare.to && daycare.to.diff(daycare.from, 'minutes') > 0
            ? daycare.to.diff(daycare.from, 'minutes')
            : daycare.service?.duration ?? 0;
    const serviceWrapper: PetServiceWrapper = {
        service: {
            id: daycare.service?.serviceId || 0,
            name: daycare.service?.serviceName || 'Daycare Service'
        },
        price: daycare.price,
        beforePrice: daycare.service?.beforePrice ?? daycare.price,
        durationInMinutes,
        color: daycare.service?.serviceColor ?? '',
        variableDuration: daycare.service?.variableDuration,
        membershipItem: daycare.membershipItem,
        enrollment: daycare.enrollment,
        businessArea: 'daycare',
        discount: itemDiscount,
        customPriceAmount: daycare.price > 0 ? daycare.price : undefined
    };
    const discount = resolveDiscountPayloadForSave(itemDiscount, serviceWrapper);
    const enrollmentId = shouldApplyMembershipRedeemOnSave(serviceWrapper) ? daycare.enrollment?.id : undefined;

    return {
        orderItemPrice: resolveOrderItemPriceForSave(serviceWrapper),
        discountAmount: discount?.amount,
        discountPercentage: discount?.percentage,
        enrollmentId
    };
}

export function resolveStandardServiceSavePricing(params: {
    price: number;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem | null;
    discount?: Discount;
    businessArea: PetServiceWrapper['businessArea'];
}): {
    orderItemPrice: number | undefined;
    discountAmount?: string;
    discountPercentage?: number;
} {
    const wrapper: PetServiceWrapper = {
        service: { id: 0, name: '' },
        price: params.price,
        beforePrice: params.price,
        customPriceAmount: params.price > 0 ? params.price : undefined,
        enrollment: params.enrollment,
        membershipItem: params.membershipItem ?? undefined,
        businessArea: params.businessArea,
        color: '',
        durationInMinutes: 0,
        discount: params.discount
    };
    const discount = resolveDiscountPayloadForSave(params.discount, wrapper);
    return {
        orderItemPrice: resolveOrderItemPriceForSave(wrapper),
        discountAmount: discount?.amount,
        discountPercentage: discount?.percentage
    };
}

export function resolveDiscountPayloadForSave(
    itemDiscount?: Discount,
    service?: PetServiceWrapper
): { amount?: string; percentage?: number } | undefined {
    if (usesMembershipDiscountFieldPreview(service)) {
        return undefined;
    }

    if (!itemDiscount || itemDiscount.type === 'none' || itemDiscount.value == null) {
        return undefined;
    }

    if (itemDiscount.value === 0) {
        return { amount: '0' };
    }

    return {
        amount: itemDiscount.type === 'amount' ? String(itemDiscount.value) : undefined,
        percentage: itemDiscount.type === 'percentage' ? itemDiscount.value : undefined
    };
}
